import React, { createRef, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import _ from 'lodash';
import { GameHeader, ResultView, ResultViewClickType, StartView } from '@components';
import { calcScale, getRandomInt } from '@utils';
import {
  BACKGROUND_RANGE_ANIMATION,
  FAIL_DELAY,
  JUMP_DELAY,
  ANIMATION_SPEED,
  MAX_ITEMS_INTERVAL,
  MAX_ITEMS_INTERVAL_STEP,
  MONEYS_RANGE
} from '@config';
import { useRemoveItems, useMoveItems, useIntersects } from './hooks';
import * as S from './styles';
import { stonesMapping } from './assets';
import audio from '../../audio/audio.mp3';

const moneyPosition = { top: 'top', bottom: 'bottom' };
const showViewsDuration = 300;
const showViewsTransitionName = 'fade-in-out';

export type StonesType = keyof typeof stonesMapping;
export type MoneyPositionType = keyof typeof moneyPosition;
export type GameStatus = 'play' | 'pause' | 'stop' | 'game-over';

export interface Saved {
  score: number;
  distance: number;
  speed: number;
}

interface Stone {
  key: string;
  type: StonesType;
  ref: RefObject<HTMLDivElement>;
  position: number;
}

interface Money {
  key: string;
  verticalPosition: MoneyPositionType;
  ref: RefObject<HTMLDivElement>;
  position: number;
}

const stoneCreate = (): Stone => {
  const key = _.uniqueId(); // Чтобы не было проблем с обновлением элемента
  const type = `stone-${getRandomInt(1, 5)}` as StonesType;
  const ref = createRef<HTMLDivElement>();
  const position = 0;
  return { key, type, ref, position };
};

const moneyCreate = (): Money => {
  const key = _.uniqueId(); // Чтобы не было проблем с обновлением элемента
  const ref = createRef<HTMLDivElement>();
  const verticalPosition = Object.keys(moneyPosition)[getRandomInt(0, 1)] as MoneyPositionType;
  const position = 0;
  return { key, verticalPosition, ref, position };
};

const ViewsAnimate = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  
  &.${showViewsTransitionName}-enter {
    opacity: 0;
  }

  &.${showViewsTransitionName}-enter-active {
    opacity: 1;
    transition: opacity ${showViewsDuration}ms ease-out;
  }

  &.${showViewsTransitionName}-exit {
    opacity: 1;
  }

  &.${showViewsTransitionName}-exit-active {
    opacity: 0;
    transition: opacity ${showViewsDuration}ms ease-out;
  }
`;

export const Game: React.FC = () => {
  const [soundPlaying, setSoundPlaying] = useState(false);
  const [gameStatus, setGameStatus] = useState<GameStatus>('stop');
  const [backgroundScale, setBackgroundScale] = useState(1);
  const [stones, setStones] = useState<Stone[]>([]);
  const [moneys, setMoneys] = useState<Money[]>([]);
  const [delay, setDelay] = useState(0);
  const [jump, setJump] = useState(false);
  const [jumpAnimationActive, setJumpAnimationActive] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);
  const [failRefSaved, setFailRefSaved] = useState<RefObject<HTMLDivElement>>();
  const [fails, setFails] = useState(0);
  const [failAnimationActive, setFailAnimationActive] = useState(false);
  const [score, setScore] = useState(0);
  const [distance, setDistance] = useState(0);
  const [speed, setSpeed] = useState(0);
  const backgroundPosition = useRef<any>(0);
  const bgRef = useRef<HTMLDivElement>(null);
  const landRef = useRef<HTMLDivElement>(null);
  const menRef = useRef<HTMLDivElement>(null);
  const startViewRef = useRef<HTMLDivElement>(null);
  const resultViewRef = useRef<HTMLDivElement>(null);
  const failTimeoutIdRef = useRef<any>(null);

  const handlerResize = () => {
    setBackgroundScale(calcScale());
  };

  const restart = () => {
    setStones([]);
    setMoneys([]);
    setDistance(0);
    setSpeed(0);
    setScore(0);
    setFails(0);
    setFailAnimationActive(false);

    backgroundPosition.current = 0;
  };

  const handlerResultViewClick = (type: ResultViewClickType) => {
    const oldSavedStr = localStorage.getItem('saved');
    let oldScore = 0;

    if (oldSavedStr) {
      let oldSaved: Saved = JSON.parse(oldSavedStr);
      oldScore = oldSaved.score;
    }

    if (score > oldScore) {
      const saved: Saved = { score, distance, speed };
      localStorage.setItem('saved', JSON.stringify(saved));
    }

    if (type === 'restart') {
      restart();
      setGameStatus('play');
    } else if (type === 'continue') {
      setGameStatus('play');
    } else {
      restart();
      setGameStatus('stop');
    }
  }

  const handlerKeyDown = useCallback((e: any) => {
    if (!jump && !buttonPressed && e.keyCode === 38) {
      setJump(true);
      setJumpAnimationActive(true);
      setButtonPressed(true);

      // Переключаем анимацию
      setTimeout(() => {
        setJumpAnimationActive(false);
      }, JUMP_DELAY / 2);

      // Прыжок закончен
      setTimeout(() => {
        setJump(false);
      }, JUMP_DELAY);
    }
  }, [buttonPressed, jump]);

  const handlerKeyUp = (e: any) => {
    if (e.keyCode === 38) {
      setButtonPressed(false);
    }
  };

  const autoPlayMusic = useCallback(() => {
    if (!soundPlaying) {
      const sound = new Audio(audio);
      sound.loop = true;
      sound.play();

      setSoundPlaying(true);
    }
  }, [soundPlaying]);

  // Устанавливаем масштаб фона
  useEffect(() => setBackgroundScale(calcScale()), []);

  // Хак запуска аудио
  useEffect(() => {
    document.addEventListener('click', autoPlayMusic);
    return () => document.removeEventListener('click', autoPlayMusic);
  }, [autoPlayMusic])

  // Движение фона
  useEffect(() => {
    const ids: number[] = [];
    let animationStep = ANIMATION_SPEED[speed - 1] && gameStatus === 'play' ? ANIMATION_SPEED[speed - 1] : 0;

    const step = () => {
      const id = requestAnimationFrame(step);
      ids.push(id);

      if (bgRef.current && landRef.current) {
        bgRef.current.style.backgroundPosition = `${backgroundPosition.current * BACKGROUND_RANGE_ANIMATION}px 100%`;
        landRef.current.style.backgroundPosition = `${backgroundPosition.current}px 100%`;
        backgroundPosition.current = backgroundPosition.current - animationStep; // TODO: Возможно получать старую позицию из css будет плавнее?
      }
    };

    step();

    return () => ids.forEach((id) => cancelAnimationFrame(id));
  }, [bgRef, speed, gameStatus]);

  // Движение камней
  useMoveItems(stones, speed, gameStatus);

  // Движение монет
  useMoveItems(moneys, speed, gameStatus);

  // Скорость и дистанция
  useEffect(() => {
    if (gameStatus === 'play') {
      const id = setInterval(() => setDistance((distance) => distance + 1), 500 / speed);
      return () => clearInterval(id);
    }
  }, [speed, gameStatus]);

  useEffect(() => {
    if (distance % 20 === 0) {
      setSpeed((speed) => speed < 6 ? speed + 1 : 6);
    }
  }, [distance])

  // Генерация камней
  useEffect(() => {
    if (gameStatus === 'play') {
      const id = setTimeout(() => {
        const stone = stoneCreate();
        setStones((stones) => [...stones, stone]);
      }, delay);

      return () => clearTimeout(id);
    }
  }, [delay, stones, gameStatus]);

  // Генерация монет
  useEffect(() => {
    if (gameStatus === 'play') {
      const id = setTimeout(() => {
        const money = moneyCreate();
        setMoneys((moneys) => [...moneys, money]);
      }, delay * MONEYS_RANGE);

      return () => clearTimeout(id);
    }
  }, [delay, moneys, gameStatus]);

  // Увеличиваем скорость добавления камней и монет
  useEffect(() => {
    setDelay(MAX_ITEMS_INTERVAL - speed * MAX_ITEMS_INTERVAL_STEP);
  }, [speed]);

  // Удаление показанных камней
  useRemoveItems((newItems) => setStones(newItems), stones);

  // Удаление показанных монет
  useRemoveItems((newItems) => setMoneys(newItems), moneys);

  // Собираем монетки
  useIntersects((index) => {
    setMoneys((moneys) => {
      const newMoneys = [...moneys];
      newMoneys.splice(index, 1);
      return newMoneys;
    });

    setScore((score) => score + 1);
  }, menRef, moneys);

  // Следим за пересечением камней
  useIntersects((index) => {
    setFailRefSaved((ref) => ref !== stones[index].ref ? stones[index].ref : ref);
  }, menRef, stones);

  useEffect(() => {
    if (failRefSaved) {
      setFails((fails) => fails + 1);
    }
  }, [failRefSaved]);

  // Обрабатываем ошибки
  useEffect(() => {
    if (fails > 3) {
      setGameStatus('game-over');
      clearTimeout(failTimeoutIdRef.current);
    }

    if (fails > 0) {
      setFailAnimationActive(true);

      failTimeoutIdRef.current = setTimeout(() => setFailAnimationActive(false), FAIL_DELAY);
      return () => clearTimeout(failTimeoutIdRef.current);
    }
  }, [fails]);

  useEffect(() => {
    window.addEventListener('resize', handlerResize);
    return () => window.removeEventListener('resize', handlerResize);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handlerKeyDown);
    return () => window.removeEventListener('keydown', handlerKeyDown);
  }, [handlerKeyDown]);

  useEffect(() => {
    window.addEventListener('keyup', handlerKeyUp);
    return () => window.removeEventListener('keyup', handlerKeyUp);
  }, [handlerKeyDown]);

  return (
    <S.Container>
      <CSSTransition
        nodeRef={startViewRef}
        in={gameStatus === 'stop'}
        timeout={showViewsDuration}
        classNames={showViewsTransitionName}
        unmountOnExit
      >
        <ViewsAnimate ref={startViewRef}>
          <StartView handlerClick={() => setGameStatus('play')} />
        </ViewsAnimate>
      </CSSTransition>
      <CSSTransition
        nodeRef={resultViewRef}
        in={gameStatus === 'game-over' || gameStatus === 'pause'}
        timeout={showViewsDuration}
        classNames={showViewsTransitionName}
        unmountOnExit
      >
        <ViewsAnimate ref={resultViewRef}>
          <ResultView
            status={gameStatus}
            score={score}
            speed={speed}
            distance={distance}
            handlerClick={(type) => handlerResultViewClick(type) }
          />
        </ViewsAnimate>
      </CSSTransition>
      {
        (gameStatus !== 'stop')
        && <>
          <GameHeader score={score} speed={speed} distance={distance} handlerClick={() => setGameStatus('pause')} />
          <S.GameBackground ref={bgRef} backgroundScale={backgroundScale} />
          {
            stones.map(({ key, type, ref  }) => (
              <S.Stone ref={ref} key={key} variant={type} />
            ))
          }
          {
            moneys.map(({ key, verticalPosition, ref  }) => (
              <S.Money ref={ref} key={key} position={verticalPosition} />
            ))
          }
              <S.Land ref={landRef} />
              <S.Men ref={menRef} jumpAnimationActive={jumpAnimationActive} gameStatus={gameStatus} />
          {
            failAnimationActive
            && <S.Fail />
          }
        </>
      }
    </S.Container>
  );
};
